/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import React, {useContext, useEffect, useState} from 'react';
import Header from '../Header/Header';
import './MobileAppSetup.scss';
import DeviceSelection from './DeviceSelection/DeviceSelection';
import {ExdsGrid, ExdsSpacing} from '@exds/core';
import RsaAuthenticatorInfo from './Rsa/RsaAuthenticatorInfo/RsaAuthenticatorInfo';
import RsaDownloadApp from './Rsa/RsaDownloadApp/RsaDownloadApp';
import CreatePin from './Rsa/CreatePin/CreatePin';
import SmartLogOnInfo from './SmartLogOn/SmartLogOnInfo/SmartLogOnInfo';
import ActivateToken from "./Rsa/ActivateToken/ActivateToken";
import VerticalStepper from './VerticalStepper/VerticalStepper';
import SmartLogOnDownloadApp from './SmartLogOn/SmartLogOnDownloadApp/SmartLogOnDownloadApp';
import TestDevice from './SmartLogOn/TestDevice/TestDevice';
import {useLocation, useNavigate} from 'react-router-dom';
import SmartLogonPairDevice from "./SmartLogOn/SmartLogonPairDevice/SmartLogonPairDevice";
import {apiPost} from "../../service/service";
import AppContext from "../../AppContext";
import {TuxLoadingIndicator} from "@tuxedo/react-component-library";
import {isNullOrEmpty, returnLandingPage} from "../../utils/utils";

export default function MobileAppSetup() {
  const {
    sidContext,
    isSmartLogonEnabledContext,
    isSoftTokenEnabledContext,
    phoneNumberContext,
    hashContext,
    transactionIdContext,
    expirationTimeContext,
    hashStepsStatusContext
  } = useContext(AppContext);
  const sid = sidContext.at(0);
  const isSoftTokenEnabledBool = isSoftTokenEnabledContext.at(0) === "true";
  const isSmartLogonEnabledBool = isSmartLogonEnabledContext.at(0) === "true";
  const phoneNumber = phoneNumberContext.at(0);
  const transactionId = transactionIdContext.at(0);
  const expirationTime = expirationTimeContext.at(0);
  const [hash, setHash] = hashContext;
  const startStep = useLocation().state?.startStep;
  const [hashStepsStatus, setHashStepsStatus] = hashStepsStatusContext;
  const [loading, setLoading] = useState(false);
  const [activeMobileAppSetupPage, setActiveMobileAppSetupPage] = useState(startStep || 'deviceSelection');
  const [skipError, setSkipError] = useState({showError: false, correlationId: '', isLoading: false});
  const navigate = useNavigate();

  const [stepsStatus, setStepsStatus] = useState({
    deviceSelection: 'active',
    rsaInfo: 'disabled',
    downloadRsa: 'disabled',
    createPin: 'disabled',
    activateToken: 'disabled',
    smartLogOn: 'disabled',
    downloadSmartLogOn: 'disabled',
    pairDevice: 'disabled',
    testDevice: 'disabled'
  });
  const [pageName, setPageName] = useState('');

  useEffect(() => {
    setLoading(true);
    if (isNullOrEmpty(hash)) {
      setLoading(false);
      returnLandingPage();
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStepStatus = (stepKey, status) => {
    setStepsStatus((prevStatus) => ({
      ...prevStatus,
      [stepKey]: status
    }));
  };

  async function updateStepStatus (stepStatus, updatedRsaSetup, updatedSloSetup, updatedSloTest, handleSuccessCall) {
    setSkipError({...skipError, isLoading: true});
    try {
      const response = await apiPost('/api/stepStatus/update', {
        sid,
        phoneNumber,
        transactionId,
        rsaSetup: hashStepsStatus.rsaSetup,
        sloSetup: hashStepsStatus.sloSetup,
        sloTest: hashStepsStatus.sloTest,
        expirationTime,
        stepStatus,
        hash
      });

      if (response.status === 200) {
        setSkipError({showError: false, correlationId: '', isLoading: false});
        setHash(response.data.hash);
        setHashStepsStatus({...hashStepsStatus,
          rsaSetup: updatedRsaSetup,
          sloSetup: updatedSloSetup,
          sloTest: updatedSloTest
        });

        if (handleSuccessCall) handleSuccessCall();
        else navigate('/finish');
      } else {
        setSkipError({showError: true, correlationId: response.correlationId || 'Unknown', isLoading: false});
      }
    } catch (error) {
      setSkipError({showError: true, correlationId: error.correlationId || 'Unknown', isLoading: false});
    }
  }

  let mainStep = 2;
  const pages = {
    'deviceSelection': {
      component: <DeviceSelection setActiveMobileAppSetupPage={setActiveMobileAppSetupPage}
        handleModalAction={updateStepStatus} skipError={skipError} setSkipError={setSkipError}
        setPageName={setPageName}
        isSoftTokenEnabledBool={isSoftTokenEnabledBool}
      />,
      currentStep: {main: 1, sub: null}
    }
  };

  if (isSoftTokenEnabledBool) {
    pages.rsaInfo = {
      component: <RsaAuthenticatorInfo setActiveMobileAppSetupPage={setActiveMobileAppSetupPage}
        setStepStatus={setStepStatus} handleModalAction={updateStepStatus} skipError={skipError}
        setSkipError={setSkipError} setPageName={setPageName}/>,
      currentStep: {main: mainStep, sub: null}
    };
    pages.downloadRsa = {
      component: <RsaDownloadApp setActiveMobileAppSetupPage={setActiveMobileAppSetupPage}
        setPageName={setPageName}/>,
      currentStep: {main: mainStep, sub: 1}
    };
    pages.createPin = {
      component: <CreatePin setActiveMobileAppSetupPage={setActiveMobileAppSetupPage}
        setPageName={setPageName} />,
      currentStep: {main: mainStep, sub: 2}
    };
    pages.activateToken = {
      component: <ActivateToken setActiveMobileAppSetupPage={setActiveMobileAppSetupPage}
        setPageName={setPageName}/>,
      currentStep: {main: mainStep, sub: 3}
    };
    mainStep++;
  }

  if (isSmartLogonEnabledBool) {
    pages.smartLogOn = {
      component: <SmartLogOnInfo setActiveMobileAppSetupPage={setActiveMobileAppSetupPage}
        handleModalAction={updateStepStatus} skipError={skipError} setSkipError={setSkipError}
        setPageName={setPageName}/>,
      currentStep: {main: mainStep, sub: null}
    };
    pages.downloadSmartLogOn = {
      component: <SmartLogOnDownloadApp setActiveMobileAppSetupPage={setActiveMobileAppSetupPage}
        setPageName={setPageName}/>,
      currentStep: {main: mainStep, sub: 1}
    };
    pages.pairDevice = {
      component: <SmartLogonPairDevice setActiveMobileAppSetupPage={setActiveMobileAppSetupPage}
        updateStepStatus={updateStepStatus} skipError={skipError} setPageName={setPageName}/>,
      currentStep: {main: mainStep, sub: 2}
    };
    pages.testDevice = {
      component: <TestDevice setPageName={setPageName}/>,
      currentStep: {main: mainStep, sub: 3}
  };
  }

  const stepsData = [
    {
      title: 'Device selection',
      status: stepsStatus.deviceSelection
    }
  ];
  if (isSoftTokenEnabledBool) {
    stepsData.push({
      title: 'RSA Authenticator',
      status: stepsStatus.rsaInfo,
      subSteps: [
        {title: 'Download app', status: stepsStatus.downloadRsa},
        {title: 'Create PIN', status: stepsStatus.createPin},
        {title: 'Activate token', status: stepsStatus.activateToken}
      ]
    });
  }

  if (isSmartLogonEnabledBool) {
    stepsData.push({
      title: 'Smart Log On',
      status: stepsStatus.smartLogOn,
      subSteps: [
        { title: 'Download app', status: stepsStatus.downloadSmartLogOn },
        { title: 'Pair device', status: stepsStatus.pairDevice },
        { title: 'Test device', status: stepsStatus.testDevice }
      ]
    });
  }

  const currentPage = pages[activeMobileAppSetupPage];

  const handleStepClick = (stepIndex) => {
    const stepKeys = Object.keys(pages);
    const stepKey = stepKeys[stepIndex - 1];
    setActiveMobileAppSetupPage(stepKey);
    setStepStatus(stepKey, 'active');
  };

  return (
    <>
      <Header userValidated currentStep={4} pageName={pageName} sid={sid}/>
      {loading && <div className="loading-spinner-validate-identity"><TuxLoadingIndicator/></div>}
      {!loading &&
        <ExdsSpacing>
          <ExdsGrid container flexDirection='row' className='mobile-app-setup-container'>
            <ExdsGrid columnSpan={4} className='vertical-stepper-container'>
              <div>
                {currentPage && <VerticalStepper steps={stepsData} currentStep={currentPage.currentStep} onStepClick={handleStepClick} sid={sid}/>}
              </div>
            </ExdsGrid>
            {currentPage && currentPage.component}
          </ExdsGrid>
        </ExdsSpacing>
      }
    </>
  );
}
